import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { Loader2, CheckCircle2, Shield } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Progress } from '@/components/ui/progress'

export default function Register() {
  const navigate = useNavigate()
  const { signUp } = useAuth()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    state: '',
    email: '',
    password: '',
    confirmPassword: ''
  })

  // Validação em tempo real
  const [validations, setValidations] = useState({
    email: { isValid: false, message: '' },
    password: { isValid: false, message: '' },
    confirmPassword: { isValid: false, message: '' }
  })

  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ]

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = []
    const particleCount = window.innerWidth < 768 ? 15 : 25 // Menos partículas no mobile

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        speedY: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        opacity: Math.random() * 0.3 + 0.1
      })
    }

    const drawXrayGrid = () => {
      ctx.strokeStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.lineWidth = 0.5
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }

      ctx.strokeStyle = 'rgba(44, 62, 80, 0.05)'
      ctx.lineWidth = 0.2
      
      for (let x = 0; x < canvas.width; x += 10) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      
      for (let y = 0; y < canvas.height; y += 10) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }
    }

    let scanLinePos = 0
    const drawScanLine = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.1)'
      ctx.fillRect(0, scanLinePos, canvas.width, 2)
      // Movimento 3x mais lento no mobile
      scanLinePos = (scanLinePos + (window.innerWidth < 768 ? 0.7 : 2)) % canvas.height
    }

    const drawMarkers = () => {
      ctx.fillStyle = 'rgba(44, 62, 80, 0.3)'
      ctx.font = '10px monospace'
      
      for (let y = 0; y < canvas.height; y += 50) {
        ctx.fillText(`${y}`, 5, y + 10)
      }
      
      for (let x = 0; x < canvas.width; x += 50) {
        ctx.fillText(`${x}`, x, 10)
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawXrayGrid()
      drawScanLine()
      drawMarkers()

      particles.forEach((particle, i) => {
        particle.x += particle.speedX
        particle.y += particle.speedY

        if (particle.x < 0) particle.x = canvas.width
        if (particle.x > canvas.width) particle.x = 0
        if (particle.y < 0) particle.y = canvas.height
        if (particle.y > canvas.height) particle.y = 0

        ctx.beginPath()
        ctx.fillStyle = `rgba(44, 62, 80, ${particle.opacity})`
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2))
          
          if (distance < 100) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(44, 62, 80, ${0.1 * (1 - distance / 100)})`
            ctx.lineWidth = 0.5
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()
          }
        }
      })

      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isValid = emailRegex.test(email)
    setValidations(prev => ({
      ...prev,
      email: {
        isValid,
        message: isValid ? '' : 'Email inválido'
      }
    }))
  }

  const validatePassword = (password: string) => {
    const isValid = password.length >= 8
    setValidations(prev => ({
      ...prev,
      password: {
        isValid,
        message: isValid ? '' : 'A senha deve ter pelo menos 8 caracteres'
      }
    }))
  }

  const validateConfirmPassword = (confirmPassword: string) => {
    const isValid = confirmPassword === formData.password
    setValidations(prev => ({
      ...prev,
      confirmPassword: {
        isValid,
        message: isValid ? '' : 'As senhas não coincidem'
      }
    }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Validação em tempo real
    if (name === 'email') validateEmail(value)
    if (name === 'password') validatePassword(value)
    if (name === 'confirmPassword') validateConfirmPassword(value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (step < 3) {
      setStep(prev => prev + 1)
      return
    }

    setLoading(true)

    try {
      await signUp({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        state: formData.state
      })

      toast({
        title: 'Conta criada com sucesso! 🎉',
        description: 'Verifique seu email para confirmar sua conta.',
      })
      navigate('/login')
    } catch (error) {
      console.error('Erro ao criar conta:', error)
      toast({
        variant: 'destructive',
        title: 'Erro ao criar conta',
        description: 'Ocorreu um erro ao criar sua conta. Tente novamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="text-center space-y-2 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">Quem é você?</h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Como você gostaria de ser chamado?
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-sm font-medium">Nome</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="h-11 bg-white/10 border-primary/20 focus:border-primary/40 transition-colors"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-sm font-medium">Sobrenome</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="h-11 bg-white/10 border-primary/20 focus:border-primary/40 transition-colors"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state" className="text-sm font-medium">Estado</Label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full h-11 px-3 rounded-md border bg-white/10 border-primary/20 focus:border-primary/40 transition-colors"
                required
              >
                <option value="">Selecione seu estado</option>
                {brazilianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        )

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="text-center space-y-2 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">Seu Email</h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Como podemos entrar em contato com você?
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`h-11 bg-white/10 border-primary/20 focus:border-primary/40 transition-colors ${
                  validations.email.message ? 'border-red-500' : ''
                }`}
                required
              />
              {validations.email.message && (
                <p className="text-sm text-red-500 mt-1">{validations.email.message}</p>
              )}
              {validations.email.isValid && (
                <div className="flex items-center gap-2 text-green-500 mt-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">Email válido</span>
                </div>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Shield className="w-5 h-5 text-primary" />
                <span className="text-sm">Fique tranquilo! Enviaremos apenas atualizações importantes e dicas exclusivas para otimizar sua experiência com a Simples</span>
              </div>
            </div>
          </motion.div>
        )

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="text-center space-y-2 mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary">Crie sua Senha</h2>
              <p className="text-sm sm:text-base text-muted-foreground">
                Última etapa! Escolha uma senha segura
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`h-11 bg-white/10 border-primary/20 focus:border-primary/40 transition-colors ${
                    validations.password.message ? 'border-red-500' : ''
                  }`}
                  required
                />
                {validations.password.message && (
                  <p className="text-sm text-red-500 mt-1">{validations.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">Confirmar Senha</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`h-11 bg-white/10 border-primary/20 focus:border-primary/40 transition-colors ${
                    validations.confirmPassword.message ? 'border-red-500' : ''
                  }`}
                  required
                />
                {validations.confirmPassword.message && (
                  <p className="text-sm text-red-500 mt-1">{validations.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <h3 className="font-medium mb-2">Sua senha deve ter:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${formData.password.length >= 8 ? 'text-green-500' : ''}`} />
                  Mínimo de 8 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : ''}`} />
                  Pelo menos uma letra maiúscula
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className={`w-4 h-4 ${/[0-9]/.test(formData.password) ? 'text-green-500' : ''}`} />
                  Pelo menos um número
                </li>
              </ul>
            </div>
          </motion.div>
        )
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent sm:animate-pulse-slow animate-pulse-slower" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 sm:animate-gradient-slow animate-gradient-slower" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>

      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.7 }}
      />

      <div className="relative container flex flex-col items-center min-h-screen px-3 xs:px-4 sm:px-6 lg:px-8">
        {/* Logo e Login Link */}
        <div className="w-full flex justify-between items-center mt-3 xs:mt-4 sm:mt-6 lg:mt-8 mb-3 xs:mb-4">
          <Link to="/" className="flex items-center gap-1.5 xs:gap-2 sm:gap-3 animate-in slide-in-from-left duration-500">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-primary shadow-lg backdrop-blur-sm bg-opacity-90">
              <svg
                className="w-6 h-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12h2l2 -8 2 16 2 -12 2 8 1.5 -3 2 0 1 -2 3.5 0" />
                <path d="M3 8h18" strokeOpacity="0.3" strokeWidth="1" />
                <path d="M3 16h18" strokeOpacity="0.3" strokeWidth="1" />
              </svg>
            </div>
            <span className="font-semibold text-xl xs:text-2xl sm:text-3xl lg:text-4xl">Simples.ECG</span>
          </Link>

          <div className="animate-in slide-in-from-right duration-500">
            <div className="flex items-center gap-1 xs:gap-1.5 sm:gap-2 px-2 xs:px-3 sm:px-4 py-1.5 xs:py-2 rounded-lg xs:rounded-xl bg-white/5 backdrop-blur-sm border border-primary/10 shadow-lg">
              <span className="text-[11px] xs:text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Já tem uma conta?</span>
              <Link 
                to="/login" 
                className="px-2 xs:px-2.5 sm:px-4 py-1 xs:py-1.5 sm:py-2 text-[11px] xs:text-xs sm:text-sm font-medium text-primary-foreground bg-primary rounded-md xs:rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                Fazer Login
              </Link>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center flex-grow w-full max-w-2xl animate-in slide-in-from-bottom duration-700">
          <div className="w-full space-y-6 backdrop-blur-sm bg-white/5 p-4 xs:p-6 sm:p-8 rounded-xl xs:rounded-2xl border border-primary/10 shadow-2xl">
            <div className="space-y-4">
              <Progress value={(step / 3) * 100} className="h-2" />
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Etapa {step} de 3</span>
                <span>{Math.round((step / 3) * 100)}% completo</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <AnimatePresence mode="wait">
                {renderStep()}
              </AnimatePresence>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(prev => prev - 1)}
                    className="w-full sm:w-auto order-2 sm:order-1"
                  >
                    Voltar
                  </Button>
                )}

                <Button 
                  type="submit" 
                  className="w-full sm:w-auto px-8 h-11 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-r from-primary/90 via-primary to-primary-200 hover:scale-[1.02] hover:brightness-110 order-1 sm:order-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando conta...
                    </>
                  ) : step < 3 ? (
                    'Próximo'
                  ) : (
                    'Criar conta'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
} 